import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, fmtEUR } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  TrendingUp, FolderKanban, Wallet, Users, FileSignature, Settings,
  ArrowRight, Sparkles,
} from 'lucide-react';

const SECTIONS = [
  {
    key: 'pilotage', title: 'Pilotage', icon: TrendingUp, color: 'blue',
    desc: 'Vue d’ensemble & reporting',
    items: [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/reporting', label: 'Reporting mensuel' },
      { to: '/analyse-temporelle', label: 'Analyse temporelle' },
      { to: '/tresorerie', label: 'Trésorerie' },
    ],
  },
  {
    key: 'projets', title: 'Projets', icon: FolderKanban, color: 'indigo',
    desc: 'Suivi opérationnel des dossiers',
    items: [
      { to: '/projets', label: 'Tous les projets' },
      { to: '/promoteurs', label: 'Clients' },
      { to: '/rentabilite', label: 'Rentabilité' },
      { to: '/liens-utiles', label: 'Liens utiles' },
    ],
  },
  {
    key: 'finance', title: 'Finance', icon: Wallet, color: 'emerald',
    desc: 'Gestion financière de l’agence',
    items: [
      { to: '/factures', label: 'Facturation' },
      { to: '/charges', label: 'Charges & Paiements' },
      { to: '/encaissements', label: 'Encaissements' },
      { to: '/provisions', label: 'Assurance — Suivi' },
      { to: '/remuneration-dirigeants', label: 'Rémun. dirigeants' },
    ],
  },
  {
    key: 'equipe', title: 'Équipe', icon: Users, color: 'amber',
    desc: 'Salariés, pointages, absences',
    items: [
      { to: '/salaries', label: 'Salariés' },
      { to: '/pointages', label: 'Pointages' },
      { to: '/absences', label: 'Absences' },
    ],
  },
  {
    key: 'outils', title: 'Outils & Documents', icon: FileSignature, color: 'violet',
    desc: 'Génération de documents',
    upcoming: true,
    items: [
      { to: null, label: 'Devis', phase: 'P3' },
      { to: null, label: 'Génération contrat', phase: 'P4' },
      { to: null, label: 'Génération CERFA PC4', phase: 'P4' },
    ],
  },
  {
    key: 'config', title: 'Configuration', icon: Settings, color: 'zinc',
    desc: 'Paramètres & roadmap',
    items: [
      { to: '/parametres', label: 'Paramètres' },
    ],
  },
];

const COLOR_MAP = {
  blue:    { bar: 'bg-[#002FA7]', text: 'text-[#002FA7]', soft: 'bg-blue-50/30',    hover: 'hover:bg-blue-50' },
  indigo:  { bar: 'bg-indigo-700', text: 'text-indigo-700', soft: 'bg-indigo-50/30', hover: 'hover:bg-indigo-50' },
  emerald: { bar: 'bg-emerald-700', text: 'text-emerald-700', soft: 'bg-emerald-50/30', hover: 'hover:bg-emerald-50' },
  amber:   { bar: 'bg-amber-600', text: 'text-amber-700', soft: 'bg-amber-50/30',   hover: 'hover:bg-amber-50' },
  violet:  { bar: 'bg-violet-700', text: 'text-violet-700', soft: 'bg-violet-50/30', hover: 'hover:bg-violet-50' },
  zinc:    { bar: 'bg-zinc-800', text: 'text-zinc-700', soft: 'bg-zinc-50',         hover: 'hover:bg-zinc-100' },
};

export default function Hub() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const annee = new Date().getFullYear();

  useEffect(() => {
    api.get(`/dashboard/kpis?annee=${annee}`).then(r => setStats(r.data)).catch(() => {});
  }, [annee]);

  const heure = new Date().getHours();
  const greet = heure < 12 ? 'Bonjour' : heure < 18 ? 'Bon après-midi' : 'Bonsoir';

  return (
    <Layout title={`${greet}, ${user?.prenom || ''}`} subtitle="Tableau de bord — choisissez une catégorie">
      {/* Bandeau résumé rapide */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="hub-stats">
          <QuickStat label={`CA ${annee}`} value={stats.ca_realise || 0} accent="emerald" />
          <QuickStat label="Projets actifs" value={stats.nb_projets_actifs || 0} unit="" />
          <QuickStat label="Résultat réalisé" value={stats.resultat_realise || 0} accent={(stats.resultat_realise || 0) >= 0 ? 'emerald' : 'rose'} />
          <QuickStat label="Heures pointées" value={stats.total_heures_pointees || 0} unit="h" />
        </div>
      )}

      {/* Grille des 6 catégories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hub-grid">
        {SECTIONS.map((s) => <SectionTile key={s.key} section={s} />)}
      </div>

      <div className="mt-8 px-4 py-3 bg-blue-50/40 border border-blue-200 text-xs text-blue-900 flex items-start gap-2 rounded-none">
        <Sparkles size={14} className="mt-0.5 shrink-0" />
        <div>
          <strong>Astuce :</strong> vous pouvez aussi naviguer directement via le menu latéral (sections repliables). Cliquez sur le logo en haut à gauche pour revenir ici à tout moment.
        </div>
      </div>
    </Layout>
  );
}

function SectionTile({ section }) {
  const { title, icon: Icon, color, desc, items, upcoming } = section;
  const c = COLOR_MAP[color];
  const firstActive = items.find(it => it.to);
  const Tag = firstActive ? Link : 'div';
  const tagProps = firstActive ? { to: firstActive.to } : {};

  return (
    <Tag
      {...tagProps}
      className={`group relative bg-white border border-zinc-200 ${c.soft} ${firstActive ? `${c.hover} cursor-pointer` : 'opacity-80'} transition-all p-5 flex flex-col`}
      data-testid={`hub-tile-${section.key}`}
    >
      <div className={`absolute top-0 left-0 w-full h-1 ${c.bar}`} />
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 ${c.bar} text-white flex items-center justify-center`}>
          <Icon size={20} strokeWidth={1.75} />
        </div>
        {upcoming && (
          <span className="text-[9px] uppercase tracking-wider font-semibold text-zinc-500 bg-zinc-100 px-2 py-1 border border-zinc-300">À venir</span>
        )}
      </div>
      <h3 className={`font-display font-bold text-xl tracking-tight ${c.text} mb-1`}>{title}</h3>
      <p className="text-xs text-zinc-500 mb-4">{desc}</p>
      <ul className="space-y-1.5 mt-auto">
        {items.map((it) => (
          <li key={it.label}>
            {it.to ? (
              <Link to={it.to} onClick={(e) => e.stopPropagation()} className="flex items-center justify-between text-sm text-zinc-700 hover:text-[#002FA7] py-1 group/item">
                <span>{it.label}</span>
                <ArrowRight size={12} className="opacity-0 group-hover/item:opacity-100 transition-opacity" />
              </Link>
            ) : (
              <div className="flex items-center justify-between text-sm text-zinc-400 py-1 italic">
                <span>{it.label}</span>
                {it.phase && <span className="text-[9px] font-mono">{it.phase}</span>}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Tag>
  );
}

function QuickStat({ label, value, unit = '€', accent = 'blue' }) {
  const accentClass = accent === 'emerald' ? 'text-emerald-700' : accent === 'rose' ? 'text-rose-600' : 'text-[#002FA7]';
  return (
    <div className="bg-white border border-zinc-200 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className={`font-mono font-medium text-xl ${accentClass}`}>
        {unit === '€' ? fmtEUR(value) : `${Math.round(value).toLocaleString('fr-FR')} ${unit}`.trim()}
      </div>
    </div>
  );
}
